import { EventStatusFilter } from 'util/types/event-status-filters';
import { pxToEm } from 'util/pxToEm';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import React, { ReactElement, useState } from 'react';
import { Event, useEventsApi } from 'services/api';
import { OrganizationSummary } from 'services/api/models';
import useSWR from 'swr';
import { EventStatus } from 'enum/event-status';
import { TrackEventImpression } from 'components/app/TrackEventImpression';
import { PickedEventCard } from 'components/pages/home/for-you/sections/PickedEventCard';
import { useOrgModal } from 'components/pages/home/for-you/shared';
import { sampleSize } from 'lodash';
import { useRouter } from 'next/router';
import { Routes } from 'services/routes';
import { SchoolLogo } from '../SchoolLogo';
import { HashTags } from './HashTags';

export interface SimilarEventsWidgetProps {
  event: Event;
}

// Match For You (PickedForYou) carousel / grid card width.
const CARD_WIDTH = 280;
const MOBILE_GAP = '12px';
const SAMPLE_AMOUNT = 3;

const gridSx = {
  display: 'grid',
  gridTemplateColumns: `repeat(auto-fill, minmax(min(100%, ${CARD_WIDTH}px), ${CARD_WIDTH}px))`,
  gap: '32px',
  width: '100%',
  justifyContent: 'center',
} as const;

const carouselSx = {
  display: 'flex',
  gap: MOBILE_GAP,
  overflowX: 'auto',
  scrollSnapType: 'x mandatory',
  WebkitOverflowScrolling: 'touch',
  // Bleed past MainLayout's 20px gutter so the next card peeks at the edge.
  mx: '-20px',
  px: '20px',
  scrollPaddingLeft: '20px',
  pb: '4px',
  width: '100%',
  boxSizing: 'border-box',
  '&::-webkit-scrollbar': { display: 'none' },
  scrollbarWidth: 'none',
} as const;

const carouselItemSx = {
  scrollSnapAlign: 'start',
  flexShrink: 0,
  width: `${CARD_WIDTH}px`,
} as const;

export const SimilarEventsWidget = ({
  event,
}: SimilarEventsWidgetProps): ReactElement => {
  const eventsApi = useEventsApi();
  const router = useRouter();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { openOrg, orgModal } = useOrgModal();

  const [page] = useState(1);
  const [search] = useState<string | null>(null);
  const [selectedFilter] = useState<EventStatusFilter>('Published');
  const [schoolId] = useState<string>(event.school.id);

  const rEvents = useSWR(
    ['/events', page, selectedFilter, search, schoolId],
    async () => {
      const statuses: EventStatus[] = ['PUBLISHED', 'UPDATED', 'RESCHEDULED'];

      const result = await eventsApi.listEvents({
        page,
        statuses,
        search,
        schoolId: event.school.id,
        timeFilter: 'recent_only',
      });

      if (result.success) return result.value;

      if (result.error.response?.status === 401) return;

      throw new Error();
    },
    {
      revalidateOnFocus: false,
      refreshInterval: 0,
    },
  );

  // Remove current event and events from other schools from the list
  const filteredEvents =
    rEvents.data?.events.filter(
      e => e.id !== event.id && e.school.id === event.school.id,
    ) || [];

  const sampledEvents = sampleSize(filteredEvents, SAMPLE_AMOUNT);

  const goToEvent = (url: string): void => {
    void router.push(url);
  };

  const renderCard = (similarEvent: Event): ReactElement => (
    <TrackEventImpression eventId={similarEvent.id}>
      <PickedEventCard
        event={similarEvent}
        onDetails={() => goToEvent(similarEvent.url)}
        onEventClick={() => goToEvent(similarEvent.url)}
        onOrgClick={(org: OrganizationSummary) => openOrg(org)}
      />
    </TrackEventImpression>
  );

  return (
    <Box
      display="flex"
      justifyContent={'center'}
      alignItems="center"
      flexDirection="column"
      maxWidth={1120}
      margin="auto"
    >
      <Typography
        style={{
          fontSize: pxToEm(24),
          fontWeight: 500,
        }}
      >
        Explore More
      </Typography>
      <Box
        mt={20}
        mb={20}
        sx={{
          '&:hover': {
            cursor: 'pointer',
          },
        }}
        onClick={() => {
          void router.push(Routes.explore(event.school.slug));
        }}
      >
        <SchoolLogo logoUrl={event.school.logoUrl} />
      </Box>
      {sampledEvents.length > 0 &&
        (isDesktop ? (
          <Box sx={gridSx}>
            {sampledEvents.map(similarEvent => (
              <Box key={similarEvent.id}>{renderCard(similarEvent)}</Box>
            ))}
          </Box>
        ) : (
          <Box sx={carouselSx}>
            {sampledEvents.map(similarEvent => (
              <Box key={similarEvent.id} sx={carouselItemSx}>
                {renderCard(similarEvent)}
              </Box>
            ))}
          </Box>
        ))}
      {<HashTags sampledEvents={sampledEvents} allEvents={filteredEvents} />}
      {orgModal}
    </Box>
  );
};
